/**
 * useResendCooldown — a client-side lockout for actions that trigger a REAL
 * outbound email (the verification-link resend, OMEGA-563).
 *
 * The backend already caps abuse (per-username `email_resend_cooldown_secs`,
 * default 60s, plus the per-IP `RateLimitMiddleware`), so a rapid re-click is
 * silently dropped server-side. But the button itself showed no cooldown, so
 * the UI kept inviting clicks the server would ignore. This hook mirrors that
 * backend window in the UI: after a successful send the caller invokes
 * `start()`, and the button stays disabled with a live per-second countdown
 * until it elapses.
 *
 * Returns `{ remaining, onCooldown, start }` — `remaining` is whole seconds
 * left (0 when ready), `onCooldown` is `remaining > 0`.
 */

import { useState, useRef, useEffect, useCallback } from "react";

// Mirror the backend default (`email_resend_cooldown_secs`). A shorter UI window
// than the server's would still invite a click the server drops, so keep them equal.
const DEFAULT_COOLDOWN_SECONDS = 60;

export default function useResendCooldown(seconds = DEFAULT_COOLDOWN_SECONDS) {
  const [remaining, setRemaining] = useState(0);
  const timerRef = useRef(null);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clear();
    setRemaining(seconds);
    timerRef.current = setInterval(() => {
      setRemaining((r) => (r <= 1 ? 0 : r - 1));
    }, 1000);
  }, [seconds, clear]);

  // Stop ticking once the countdown drains, and always clean up on unmount so a
  // component that unmounts mid-cooldown never leaves a dangling interval.
  useEffect(() => {
    if (remaining === 0) clear();
  }, [remaining, clear]);

  useEffect(() => clear, [clear]);

  return { remaining, onCooldown: remaining > 0, start };
}
